/**
 * AI assistant inbox on the host side.
 *
 * The embedded chat widget owns the server-side inbox (`/me/notifications`:
 * scheduled-task results, in-app reminders) and mirrors it to us with an
 * `ai-notifications` postMessage every time it changes. AiAssistantDrawer
 * feeds those into this tiny module store; the header bell
 * (`NotificationSection`) subscribes, so a task that finished while the chat
 * was collapsed — or while the tab was closed — still shows up in the ERP.
 *
 * Consuming an item (`markRead`) goes back the same way: a window event the
 * drawer turns into an `ai-notifications-read` postMessage for the iframe,
 * which calls the API and re-broadcasts the shrunken list.
 */
import { useCallback, useEffect, useState } from 'react';
import { aiOpenThread, aiOpenView } from './useAiAssistant';

export interface AiHostNotification {
    id: string;
    kind: 'task_done' | 'task_failed' | 'reminder' | 'info' | string;
    severity: 'info' | 'success' | 'warning' | 'error';
    title: string;
    text: string;
    thread_id?: string | null;
    task_id?: string | null;
    created_at: string;
}

export const AI_NOTIFICATIONS_READ_EVENT = 'ai-notifications-read';

let items: AiHostNotification[] = [];
const listeners = new Set<(next: AiHostNotification[]) => void>();

function emit(): void {
    listeners.forEach((fn) => { try { fn(items); } catch { /* */ } });
}

/** Called by AiAssistantDrawer on every `ai-notifications` message. */
export function setAiNotifications(next: unknown): void {
    if (!Array.isArray(next)) return;
    items = next
        .filter((n) => n && typeof n === 'object' && typeof (n as any).id === 'string')
        .map((n: any) => ({
            id: String(n.id),
            kind: String(n.kind || 'info'),
            severity: (['info', 'success', 'warning', 'error'].includes(n.severity) ? n.severity : 'info'),
            title: String(n.title || ''),
            text: String(n.text || ''),
            thread_id: n.thread_id ? String(n.thread_id) : null,
            task_id: n.task_id ? String(n.task_id) : null,
            created_at: String(n.created_at || ''),
        }));
    emit();
}

export function getAiNotifications(): AiHostNotification[] {
    return items;
}

/** Empty / omitted ids = mark everything read. */
export function aiMarkNotificationsRead(ids?: string[]): void {
    const clean = (ids || []).filter(Boolean);
    // Optimistic: drop locally right away, the iframe re-broadcasts the truth.
    items = clean.length ? items.filter((n) => !clean.includes(n.id)) : [];
    emit();
    try {
        window.dispatchEvent(new CustomEvent(AI_NOTIFICATIONS_READ_EVENT, { detail: { ids: clean } }));
    } catch { /* */ }
}

export function useAiNotifications() {
    const [list, setList] = useState<AiHostNotification[]>(items);
    useEffect(() => {
        listeners.add(setList);
        setList(items);
        return () => { listeners.delete(setList); };
    }, []);

    const open = useCallback((n: AiHostNotification) => {
        if (n.thread_id) aiOpenThread(n.thread_id);
        else aiOpenView('scheduler');
        aiMarkNotificationsRead([n.id]);
    }, []);

    const markAllRead = useCallback(() => aiMarkNotificationsRead(), []);
    const markRead = useCallback((ids: string[]) => aiMarkNotificationsRead(ids), []);

    return { items: list, unread: list.length, open, markRead, markAllRead };
}
